import { useState, type ReactNode } from 'react'
import {
  Box, Button, Checkbox, CloseButton, Drawer, HStack, NativeSelect, Popover, Portal, Text, VStack,
} from '@chakra-ui/react'
import { FiChevronDown, FiChevronRight } from 'react-icons/fi'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { useScripts } from '../../../hooks/useScript'
import { useAddOpportunisticStop, useRemoveOpportunisticStop, useUpdateStopScript } from '../../../hooks/useTurf'
import { extractApiMessage } from '../../../lib/apiError'
import type { GhostDto, OpportunisticStopDto } from '../../../types/turf'
import { NewScriptStub } from './NewScriptStub'
import { positionCounts } from './ghostUtils'

const NEW_SCRIPT = '__new__'

export interface GhostCardDone {
  /** Set after a successful add: the people now on the turf (the page prunes them from other ghosts). */
  addedContactIds: string[]
}

interface GhostCardProps {
  /** A suggestion from the corridor search (Add to turf)… */
  ghost?: GhostDto
  /** …or an added stop, from a diamond click (change script / Remove from turf). */
  stop?: OpportunisticStopDto
  turfId: string
  /** Close; carries the added people after a successful add. */
  onDone: (result?: GhostCardDone) => void
  /** Desktop only: where the clicked marker is, in pixels within the map container (position: relative). */
  anchor?: { x: number; y: number }
}

/** [O4] The card behind a ghost or an add-in diamond. Popover at the marker on desktop, bottom sheet on mobile. */
export function GhostCard({ anchor, onDone, ...props }: GhostCardProps) {
  const isMobile = useIsMobile()
  const title = props.ghost?.label ?? props.stop?.label ?? ''
  const body = <GhostCardBody {...props} onDone={onDone} />

  if (isMobile) {
    return (
      <Drawer.Root open placement="bottom" onOpenChange={(e) => { if (!e.open) onDone() }}>
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content borderTopRadius="xl" maxH="85vh" pb="env(safe-area-inset-bottom, 0px)" shadow="popover"
              aria-label={title}>
              <Drawer.CloseTrigger asChild><CloseButton size="sm" minW="44px" minH="44px" /></Drawer.CloseTrigger>
              <Drawer.Body overflowY="auto" pt={4} pb={4}>{body}</Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    )
  }

  return (
    <Popover.Root open onOpenChange={(e) => { if (!e.open) onDone() }} positioning={{ placement: 'right-start', gutter: 12 }}>
      <Popover.Anchor asChild>
        <Box position="absolute" left={`${anchor?.x ?? 0}px`} top={`${anchor?.y ?? 0}px`} w="1px" h="1px" pointerEvents="none" />
      </Popover.Anchor>
      <Portal>
        <Popover.Positioner>
          <Popover.Content w="320px" shadow="popover" aria-label={title}>
            <Popover.CloseTrigger asChild position="absolute" top={1} right={1}><CloseButton size="sm" /></Popover.CloseTrigger>
            <Popover.Body p={4} maxH="70vh" overflowY="auto">{body}</Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <VStack align="stretch" gap={1}>
      <Text fontSize="xs" fontWeight="medium" color="fg.muted" textTransform="uppercase">{label}</Text>
      {children}
    </VStack>
  )
}

function GhostCardBody({ ghost, stop, turfId, onDone }: Omit<GhostCardProps, 'anchor'>) {
  const isExtras = ghost?.kind === 'ExistingStopExtras'
  // New sites start with everyone checked (uncheck to drop); extras were maybe filtered out on purpose.
  const [checked, setChecked] = useState<Set<string>>(
    () => new Set(isExtras || !ghost ? [] : ghost.people.map(p => p.contactId)))
  const [showNames, setShowNames] = useState(isExtras)
  const [scriptId, setScriptId] = useState<string | null>(stop?.scriptId ?? null)
  const [created, setCreated] = useState<{ id: string; label: string } | null>(null)
  const [creating, setCreating] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [addError, setAddError] = useState<unknown>(null)
  const [scriptError, setScriptError] = useState<unknown>(null)
  const [removeError, setRemoveError] = useState<unknown>(null)

  const { data: scripts } = useScripts()
  const { mutateAsync: addStop, isPending: adding } = useAddOpportunisticStop(turfId)
  const { mutateAsync: updateStopScript } = useUpdateStopScript(turfId)
  const { mutateAsync: removeStop, isPending: removing } = useRemoveOpportunisticStop(turfId)

  // The committee's active scripts, plus whatever is currently chosen (a just-created stub the list
  // hasn't refetched yet, or a stop's override that has since been deactivated).
  const options = (scripts ?? []).filter(s => s.isActive || s.id === scriptId).map(s => ({ id: s.id, label: s.label }))
  if (created && !options.some(o => o.id === created.id)) options.push(created)
  if (stop?.scriptId && stop.scriptLabel && !options.some(o => o.id === stop.scriptId)) {
    options.push({ id: stop.scriptId, label: stop.scriptLabel })
  }

  const applyScript = async (next: string | null) => {
    setScriptError(null)
    if (!stop) { setScriptId(next); return }
    const prev = scriptId
    setScriptId(next)
    try {
      await updateStopScript({ stopId: stop.id, scriptId: next })
    } catch (err) {
      setScriptId(prev)
      setScriptError(err)
    }
  }

  const add = async () => {
    if (!ghost) return
    const contactIds = ghost.people.filter(p => checked.has(p.contactId)).map(p => p.contactId)
    if (contactIds.length === 0) return
    setAddError(null)
    try {
      await addStop({ worksiteId: ghost.worksiteId, contactIds, scriptId })
      onDone({ addedContactIds: contactIds })
    } catch (err) {
      setAddError(err)
    }
  }

  const remove = async () => {
    if (!stop) return
    setRemoveError(null)
    try {
      await removeStop(stop.id)
      onDone()
    } catch (err) {
      setRemoveError(err)
    }
  }

  const toggle = (contactId: string, on: boolean) => setChecked(prev => {
    const next = new Set(prev)
    if (on) next.add(contactId)
    else next.delete(contactId)
    return next
  })

  const address = ghost
    ? [ghost.streetAddress, [ghost.city, ghost.zip].filter(Boolean).join(' ')].filter(Boolean).join(' · ')
    : null
  const business = ghost?.businessName && ghost.businessName !== ghost.label ? ghost.businessName : null

  return (
    <VStack align="stretch" gap={3} pr={6}>
      {/* Header — the WorkplacePopup look: green name, gray address, stats line */}
      <Box>
        <Text fontWeight="bold" color="accent">
          {ghost?.label ?? stop?.label}
          {business && <Text as="span" fontWeight="normal" color="fg.muted"> ({business})</Text>}
        </Text>
        {address && <Text fontSize="sm" color="fg.muted">{address}</Text>}
        <Text fontSize="sm" color="fg.muted">
          {ghost
            ? `${ghost.people.length} people on file · not in this turf's universe`
            : `${stop!.memberCount} people · added stop`}
        </Text>
      </Box>

      {ghost && ghost.kind !== 'Home' && (
        <Section label="Who's there">
          <Box as="ul" aria-label="Who's there" listStyleType="none" m={0} p={0}>
            {positionCounts(ghost.people).map(({ position, count }) => (
              <HStack as="li" key={position} justify="space-between" fontSize="sm">
                <Text>{position}</Text>
                <Text color="fg.muted">{count}</Text>
              </HStack>
            ))}
          </Box>
        </Section>
      )}

      {ghost && (
        <Box>
          <Button size="sm" variant="ghost" px={1} minH={{ base: '44px', md: 'auto' }}
            aria-expanded={showNames} onClick={() => setShowNames(v => !v)}>
            {showNames ? <FiChevronDown /> : <FiChevronRight />}
            {showNames ? 'Hide names' : 'Show names'}
          </Button>
          {showNames && (
            <VStack align="stretch" gap={0} maxH={{ base: '40vh', md: '220px' }} overflowY="auto"
              borderWidth="1px" borderColor="border" borderRadius="md" px={2}>
              {ghost.people.map(p => (
                <HStack key={p.contactId} justify="space-between" minH={{ base: '44px', md: '32px' }} gap={2}>
                  <Checkbox.Root size="sm" colorPalette="brand" checked={checked.has(p.contactId)}
                    onCheckedChange={(e) => toggle(p.contactId, !!e.checked)}>
                    <Checkbox.HiddenInput />
                    <Checkbox.Control><Checkbox.Indicator /></Checkbox.Control>
                    <Checkbox.Label>{p.contactName}</Checkbox.Label>
                  </Checkbox.Root>
                  {p.position && <Text fontSize="xs" color="fg.muted" truncate>{p.position}</Text>}
                </HStack>
              ))}
            </VStack>
          )}
        </Box>
      )}

      <Section label="Script for this stop">
        <NativeSelect.Root size="sm">
          <NativeSelect.Field
            aria-label="Script for this stop"
            minH={{ base: '44px', md: 'auto' }}
            value={scriptId ?? ''}
            onChange={(e) => {
              if (e.target.value === NEW_SCRIPT) { setCreating(true); return }
              void applyScript(e.target.value || null)
            }}
          >
            <option value="">Campaign's script (as usual)</option>
            {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            <option value={NEW_SCRIPT}>+ New script…</option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        {creating && (
          <NewScriptStub
            onCancel={() => setCreating(false)}
            onCreated={(id, label) => {
              setCreated({ id, label })
              setCreating(false)
              void applyScript(id)
            }}
          />
        )}
        {!!scriptError && (
          <Text fontSize="sm" color="danger.fg">{extractApiMessage(scriptError, 'Could not change the script')}</Text>
        )}
      </Section>

      {ghost && (
        <VStack align="stretch" gap={2}>
          <HStack justify="flex-end" gap={2}>
            <Button size="sm" variant="ghost" minH={{ base: '44px', md: 'auto' }} onClick={() => onDone()}>Skip</Button>
            <Button size="sm" colorPalette="brand" minH={{ base: '44px', md: 'auto' }}
              disabled={checked.size === 0} loading={adding} onClick={() => void add()}>
              Add to turf
            </Button>
          </HStack>
          {!!addError && <Text fontSize="sm" color="danger.fg">{extractApiMessage(addError, 'Could not add this stop')}</Text>}
        </VStack>
      )}

      {stop && (
        <VStack align="stretch" gap={2}>
          {confirmRemove ? (
            <Box borderWidth="1px" borderColor="border" borderRadius="md" p={3}>
              <Text fontSize="sm" mb={2}>Remove this stop and its {stop.memberCount} people from the turf?</Text>
              <HStack justify="flex-end" gap={2}>
                <Button size="sm" variant="ghost" minH={{ base: '44px', md: 'auto' }} onClick={() => setConfirmRemove(false)}>
                  Cancel
                </Button>
                <Button size="sm" colorPalette="red" minH={{ base: '44px', md: 'auto' }} loading={removing}
                  onClick={() => void remove()}>
                  Remove
                </Button>
              </HStack>
            </Box>
          ) : (
            <HStack justify="flex-end">
              <Button size="sm" colorPalette="red" variant="outline" minH={{ base: '44px', md: 'auto' }}
                onClick={() => setConfirmRemove(true)}>
                Remove from turf
              </Button>
            </HStack>
          )}
          {!!removeError && <Text fontSize="sm" color="danger.fg">{extractApiMessage(removeError, 'Could not remove this stop')}</Text>}
        </VStack>
      )}
    </VStack>
  )
}
